import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { TailSpin } from 'react-loading-icons';
import {
  app,
  cacheHomepageSnapshot,
  buildHomepageSnapshotFromProfile,
  cacheUserProfileSnapshot,
  canAccessProfileFromProfile,
  completeFlowEmailPasswordAuth,
  completeFlowSignInWithApple,
  completeFlowSignInWithGoogle,
  consumePendingAuthRedirectResult,
  consumePendingEmailLinkSignIn,
  completePendingEmailLinkSignIn,
  createSubscriptionCheckoutSession,
  createShareLink,
  debugResultAccess,
  getAnalyticsEventContext,
  isSubscriptionActiveFromProfile,
  logAnalyticsEvent,
  openDeferredExternalUrl,
  sendEmailSignInLinkForFlow,
  signOutCurrentUser,
  shouldPreferMagicLinkEmailAuth,
  hasResolvedInitialAuthState,
  onAuthUserChanged,
  getCurrentUser,
  subscribeToUserProfile,
  downloadMediaViaBackend,
  waitForInitialAuthState,
} from '../Firebase';
import AppHeader, { appHeaderActionClasses } from '../AppHeader';
import LegalFooter from '../LegalFooter';
import { getFallbackResultHomeState } from '../homeState.js';
import AuthGateStep from '../UploadPhotos/AuthGateStep.jsx';
import { getModernResultUrl } from '../resultPaths.js';

const isValidEmail = (value) => /\S+@\S+\.\S+/.test(value || '');
const normalizePetName = (value) =>
  (value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const inferProgress = (documentData) => {
  const explicitProgress = Number(documentData?.generation_progress);
  if (Number.isFinite(explicitProgress) && explicitProgress >= 0) {
    return Math.min(100, Math.max(0, explicitProgress));
  }

  const status = (documentData?.generation_status || '').toLowerCase();
  if (status === 'queued') return 5;
  if (status === 'generating_images') return 35;
  if (status === 'generating_videos') return 75;
  if (status === 'completed') return 100;
  if (status === 'failed') return 0;
  return 10;
};

const statusLabel = (documentData) => {
  const status = (documentData?.generation_status || '').toLowerCase();
  if (status === 'queued') return 'Queued';
  if (status === 'generating_images') return 'Generating images';
  if (status === 'generating_videos') return 'Generating videos';
  if (status === 'completed') return 'Completed';
  if (status === 'failed') return 'Failed';
  return 'Starting';
};

const getCompletedPreviewVideoUrls = (docData) => {
  const outputVideos = Array.isArray(docData?.output_videos)
    ? docData.output_videos.filter((url) => typeof url === 'string' && url.trim())
    : [];
  if (outputVideos.length > 0) {
    return outputVideos;
  }
  if (typeof docData?.preview_video_url === 'string' && docData.preview_video_url.trim()) {
    return [docData.preview_video_url.trim()];
  }
  return [];
};

const getPaywallPreviewVideoUrl = (docData) => {
  const previewVideoUrl = typeof docData?.preview_video_url === 'string'
    ? docData.preview_video_url.trim()
    : '';
  if (previewVideoUrl) {
    return previewVideoUrl;
  }

  const outputVideos = Array.isArray(docData?.output_videos)
    ? docData.output_videos.filter((url) => typeof url === 'string' && url.trim())
    : [];
  return outputVideos[0] || '';
};

const RESULT_STAGE_VIEW_EVENT_NAMES = {
  starting: 'fluffball-result-starting-viewed',
  queued: 'fluffball-result-queued-viewed',
  generating_images: 'fluffball-result-generating-images-viewed',
  generating_videos: 'fluffball-result-generating-videos-viewed',
  ready_paywall: 'fluffball-result-ready-paywall-viewed',
  ready_unlocked: 'fluffball-result-ready-unlocked-viewed',
  failed: 'fluffball-result-failed-viewed',
};

const RESULT_STAGE_VIEW_ORDER = {
  starting: 0,
  queued: 1,
  generating_images: 2,
  generating_videos: 3,
  ready_paywall: 4,
  ready_unlocked: 5,
  failed: 6,
};
const MAX_RESULT_ASSET_COUNT = 3;

const PAYWALL_PLANS = [
  {
    key: 'yearly',
    label: 'Yearly',
    sublabel: '$39.99 billed annually',
    priceLabel: '$0.77',
    priceSuffix: '/wk',
    badge: 'Save 89%',
  },
  {
    key: 'weekly',
    label: 'Weekly',
    sublabel: '',
    priceLabel: '$6.99',
    priceSuffix: '/wk',
    badge: '',
  },
];

const preloadVideoMetadata = (url) => new Promise((resolve) => {
  if (typeof document === 'undefined' || !url) {
    resolve();
    return;
  }

  const video = document.createElement('video');
  let settled = false;
  const cleanup = () => {
    video.removeEventListener('loadeddata', handleDone);
    video.removeEventListener('canplay', handleDone);
    video.removeEventListener('error', handleDone);
    video.src = '';
  };
  const handleDone = () => {
    if (settled) {
      return;
    }
    settled = true;
    cleanup();
    resolve();
  };

  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.addEventListener('loadeddata', handleDone);
  video.addEventListener('canplay', handleDone);
  video.addEventListener('error', handleDone);
  video.src = url;
  video.load();
  window.setTimeout(handleDone, 3500);
});

const SimpleSpinnerScreen = ({ fullscreen = false }) => (
  <div className={`${fullscreen ? 'min-h-screen bg-white' : 'min-h-[44vh]'} flex items-center justify-center px-4 py-8`}>
    <TailSpin stroke="#8B5CF6" speed={0.75} width="48" />
  </div>
);

const MediaCard = ({
  url,
  locked,
  type,
  posterUrl,
  canDownload,
  onDownload,
  onDownloadAll,
  downloadAllDisabled,
  showShare,
  onShare,
  shareLabel,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) {
      return () => {};
    }

    const onPointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [menuOpen]);

  return (
    <div>
      <div className="relative rounded-xl overflow-hidden shadow-md border border-gray-200 bg-white group">
        {type === 'image' ? (
          <img
            src={url}
            alt="Generated pet"
            className={`w-full aspect-[9/16] object-cover ${locked ? 'blur-xl' : ''}`}
          />
        ) : (
          <video
            src={url}
            poster={posterUrl || undefined}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className={`w-full aspect-[9/16] object-cover ${locked ? 'blur-xl' : ''}`}
          />
        )}

        {canDownload && !locked ? (
          <div ref={menuRef} className="absolute top-2 right-2">
            <button
              type="button"
              onClick={() => setMenuOpen((value) => !value)}
              className="w-8 h-8 rounded-full bg-black/70 text-white hover:bg-black/85 flex items-center justify-center"
              aria-label="Open media menu"
            >
              <span className="flex items-center justify-center gap-[3px]" aria-hidden="true">
                <span className="w-1 h-1 rounded-full bg-white" />
                <span className="w-1 h-1 rounded-full bg-white" />
                <span className="w-1 h-1 rounded-full bg-white" />
              </span>
            </button>
            {menuOpen ? (
              <div className="absolute right-0 mt-2 w-44 rounded-lg border border-gray-200 bg-white shadow-lg z-20 overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onDownload();
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50"
                >
                  Download this {type === 'image' ? 'image' : 'video'}
                </button>
                {onDownloadAll ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onDownloadAll();
                    }}
                    disabled={Boolean(downloadAllDisabled)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-60"
                  >
                    Download all videos and photos
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {showShare && !locked ? (
        <button
          type="button"
          onClick={onShare}
          className="mt-2 w-full px-3 py-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm font-semibold shadow-md hover:from-pink-600 hover:to-purple-600 transition-colors"
        >
          {shareLabel || 'Share'}
        </button>
      ) : null}
    </div>
  );
};

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { docId } = useParams();
  const initialRouteDocData = useMemo(() => {
    const routeState = location.state && typeof location.state === 'object' ? location.state : null;
    const routeDocId = String(routeState?.initialResultDocId || '').trim();
    if (!routeState || routeDocId !== String(docId || '').trim()) {
      return null;
    }
    return routeState.initialResultData && typeof routeState.initialResultData === 'object'
      ? routeState.initialResultData
      : null;
  }, [docId, location.state]);
  const initialSignedInUser = getCurrentUser();
  const hasInitialSignedInUser = Boolean(initialSignedInUser && !initialSignedInUser.isAnonymous);
  const [authUser, setAuthUser] = useState(() => {
    return hasInitialSignedInUser ? initialSignedInUser : null;
  });
  const [authLoading, setAuthLoading] = useState(
    () => !(hasInitialSignedInUser || hasResolvedInitialAuthState())
  );
  const [authFirestoreReady, setAuthFirestoreReady] = useState(
    () => hasInitialSignedInUser && hasResolvedInitialAuthState()
  );
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [documentData, setDocumentData] = useState(() => initialRouteDocData);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(() => !initialRouteDocData);
  const [error, setError] = useState(null);
  const [authInfo, setAuthInfo] = useState('');
  const [emailLinkPending, setEmailLinkPending] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [paywallError, setPaywallError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [shareStatus, setShareStatus] = useState('');
  const [downloadStatus, setDownloadStatus] = useState('');
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [resultViewLogged, setResultViewLogged] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(() => (
    typeof document === 'undefined' ? true : document.visibilityState === 'visible'
  ));
  const [videoPresentationReady, setVideoPresentationReady] = useState(false);
  const highestViewedResultStageRef = useRef(-1);
  const paywallPlanLoggedRef = useRef('');
  const checkoutReturnLoggedRef = useRef('');
  const authRedirectHandledRef = useRef(false);
  const prefersMagicLinkEmailAuth = shouldPreferMagicLinkEmailAuth();

  const trackResultsEvent = useCallback((eventName, properties = {}, userOverride = null) => {
    const analyticsContext = getAnalyticsEventContext(userOverride || authUser);
    logAnalyticsEvent({
      analyticsContext,
      eventName,
      properties: {
        screen: 'result',
        doc_id: docId,
        ...properties,
      },
    });
  }, [authUser, docId]);

  useEffect(() => {
    let isMounted = true;

    waitForInitialAuthState().then((user) => {
      if (!isMounted) {
        return;
      }

      setAuthUser(user && !user.isAnonymous ? user : null);
      setAuthLoading(false);
    });

    const unsubscribe = onAuthUserChanged((user) => {
      if (!isMounted) {
        return;
      }

      setAuthUser(user && !user.isAnonymous ? user : null);
      setAuthLoading(false);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return () => {};
    }

    const handleVisibilityChange = () => {
      setIsPageVisible(document.visibilityState === 'visible');
    };

    handleVisibilityChange();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (authLoading) {
      return () => {
        isMounted = false;
      };
    }

    if (!authUser) {
      setAuthFirestoreReady(false);
      return () => {
        isMounted = false;
      };
    }

    setAuthFirestoreReady(false);
    authUser.getIdToken()
      .then(() => {
        if (!isMounted) {
          return;
        }
        setAuthFirestoreReady(true);
      })
      .catch((tokenError) => {
        if (!isMounted) {
          return;
        }
        console.error('Failed to prepare Firebase auth token for results page:', tokenError);
        setAuthFirestoreReady(false);
      });

    return () => {
      isMounted = false;
    };
  }, [authLoading, authUser]);

  useEffect(() => {
    if (!authUser || !authFirestoreReady) {
      setUserProfile(null);
      return () => {};
    }

    return subscribeToUserProfile(authUser.uid, (profileData) => {
      setUserProfile(profileData || null);
      cacheUserProfileSnapshot(authUser, profileData || null);
    });
  }, [authFirestoreReady, authUser]);

  useEffect(() => {
    if (authLoading || !authFirestoreReady) {
      return () => {};
    }

    if (!authUser) {
      setLoading(false);
      setError(null);
      setDocumentData(null);
      return () => {};
    }

    if (!initialRouteDocData) {
      setLoading(true);
    } else {
      setLoading(false);
    }
    setError(null);

    const db = getFirestore(app);
    const docRef = doc(db, "petPhotos", docId);
    const profileSubscriptionActive = isSubscriptionActiveFromProfile(userProfile);

    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (!docSnap.exists()) {
          setError("Document not found");
          setDocumentData(null);
          setLoading(false);
          return;
        }

        const data = docSnap.data();
        const ownerUid = data?.owner_uid || '';
        const ownerEmail = (data?.owner_email || data?.email || '').trim().toLowerCase();
        const signedInEmail = (authUser?.email || '').trim().toLowerCase();

        const hasOwnerUid = Boolean(ownerUid);
        const hasOwnerEmail = Boolean(ownerEmail);
        const uidMatches = hasOwnerUid && ownerUid === authUser.uid;
        const emailMatches = hasOwnerEmail && signedInEmail === ownerEmail;

        const profileResultIds = Array.isArray(userProfile?.result_doc_ids)
          ? userProfile.result_doc_ids
          : [];

        const canAccess =
          (hasOwnerUid && uidMatches) ||
          profileResultIds.includes(docId) ||
          (!hasOwnerUid && hasOwnerEmail && emailMatches) ||
          (!hasOwnerUid && !hasOwnerEmail);

        if (!canAccess) {
          setError("This result is linked to a different account. Sign in with the account that created it.");
          setDocumentData(null);
          setLoading(false);
          return;
        }

        setDocumentData(data);
        const docHasOutput =
          String(data?.generation_status || '').toLowerCase() === 'completed' &&
          getCompletedPreviewVideoUrls(data).length > 0;
        if (docHasOutput) {
          const explicitHomeState = String(userProfile?.home_state || '').trim();
          if (explicitHomeState) {
            cacheHomepageSnapshot(
              authUser,
              buildHomepageSnapshotFromProfile(authUser, userProfile)
            );
          } else {
            const fallbackHeroState = getFallbackResultHomeState({
              subscriptionActive: profileSubscriptionActive,
              documentData: data,
            });
            cacheHomepageSnapshot(authUser, {
              hasCompletedResult: true,
              latestResultDocId: docId,
              homeActionDocId: docId,
              subscriptionActive: profileSubscriptionActive,
              canCreateDailyFromHome: fallbackHeroState === 'daily_available',
              heroState: fallbackHeroState,
            });
          }
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error subscribing to document:", err);
        if (String(err?.code || '').toLowerCase() === 'permission-denied' && authUser) {
          const tokenEmail = typeof authUser.email === 'string' ? authUser.email.trim().toLowerCase() : '';
          authUser.getIdTokenResult()
            .then((tokenResult) => debugResultAccess({ docId })
              .then((debugData) => {
                console.error('Result access diagnostics:', {
                  docId,
                  authUser: {
                    uid: authUser.uid,
                    email: tokenEmail,
                  },
                  tokenClaims: {
                    email: tokenResult?.claims?.email || '',
                    email_verified: tokenResult?.claims?.email_verified || false,
                    sign_in_provider: tokenResult?.signInProvider || '',
                  },
                  backendDebug: debugData,
                });
              }))
            .catch((debugError) => {
              console.error('Failed to collect result access diagnostics:', debugError);
            });
        }
        setError("Error loading results");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authFirestoreReady, authLoading, authUser, docId, initialRouteDocData, userProfile]);

  const videoUrls = useMemo(() => {
    const outputVideos = Array.isArray(documentData?.output_videos) ? documentData.output_videos : [];
    if (outputVideos.length) return outputVideos;
    if (documentData?.preview_video_url) return [documentData.preview_video_url];
    return [];
  }, [documentData]);
  const imageUrls = useMemo(() => {
    if (!Array.isArray(documentData?.output_images)) {
      return [];
    }
    return documentData.output_images.filter((url) => typeof url === 'string' && url.trim());
  }, [documentData]);
  const displayedVideoUrls = useMemo(() => videoUrls.slice(0, MAX_RESULT_ASSET_COUNT), [videoUrls]);
  const displayedImageUrls = useMemo(() => imageUrls.slice(0, MAX_RESULT_ASSET_COUNT), [imageUrls]);
  const paywallPreviewVideoUrl = useMemo(() => getPaywallPreviewVideoUrl(documentData), [documentData]);
  const checkoutEmail = (
    authUser?.email ||
    documentData?.owner_email ||
    documentData?.email ||
    ''
  ).trim().toLowerCase();

  const legacyUnlocked = Boolean(
    documentData?.has_paid ||
    documentData?.payment_status === 'paid' ||
    documentData?.is_unlocked ||
    documentData?.purchase_unlocked
  );
  const subscriptionActive = isSubscriptionActiveFromProfile(userProfile);
  const canAccessProfile = canAccessProfileFromProfile(userProfile);
  const subscriptionUnlockSource = String(documentData?.unlock_source || '').toLowerCase() === 'subscription';
  const subscriptionAccessOverride =
    typeof documentData?.subscription_access_active === 'boolean'
      ? documentData.subscription_access_active
      : null;
  const unlocked = subscriptionUnlockSource
    ? (subscriptionAccessOverride ?? subscriptionActive)
    : (legacyUnlocked || subscriptionActive);

  const generationStatus = String(documentData?.generation_status || '').toLowerCase();
  const generationFailed = generationStatus === 'failed';
  const hasVideoOutput = videoUrls.length > 0;
  const hasImageOutput = imageUrls.length > 0;
  const hasAnyOutput = hasVideoOutput;
  const hasStableVideoSet = hasVideoOutput && (
    generationStatus === 'completed' || displayedVideoUrls.length >= MAX_RESULT_ASSET_COUNT
  );
  const holdVideoReveal = hasVideoOutput && !generationFailed && (!hasStableVideoSet || !videoPresentationReady);
  const showImagePreviewsInFlight = hasImageOutput && !generationFailed && (!hasVideoOutput || holdVideoReveal);
  const isSingleImageLayout = displayedImageUrls.length === 1;
  const isSingleVideoLayout = displayedVideoUrls.length === 1;
  const showMobilePinnedPaywall = hasVideoOutput && !holdVideoReveal && !unlocked;
  const progress = holdVideoReveal ? Math.max(88, inferProgress(documentData)) : inferProgress(documentData);
  const petBaby = (documentData?.petType || '').toLowerCase() === 'cat' ? 'kitten' : 'puppy';
  const rawPetName = normalizePetName(
    typeof documentData?.pet_name === 'string' ? documentData.pet_name : ''
  );
  const generatingHeading = rawPetName
    ? `We're creating ${rawPetName}'s ${petBaby} videos`
    : `We're creating your pet's ${petBaby} videos`;
  const sanitizedPetName = (rawPetName || 'fluffball')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'fluffball';
  const unlockButtonText = 'Continue';
  const paywallHeading = rawPetName ? `Unlock your ${rawPetName} videos` : 'Unlock your videos';
  const shareId = String(documentData?.share_id || '').trim();
  const shareUrl = shareId ? `${window.location.origin}/share/${shareId}` : '';
  const docHasCompletedOutput =
    generationStatus === 'completed' &&
    getCompletedPreviewVideoUrls(documentData).length > 0;
  const visibleResultStage = useMemo(() => {
    if (generationFailed) {
      return 'failed';
    }
    if (hasVideoOutput && !holdVideoReveal) {
      return unlocked ? 'ready_unlocked' : 'ready_paywall';
    }
    if (generationStatus === 'queued') {
      return 'queued';
    }
    if (generationStatus === 'generating_images') {
      return 'generating_images';
    }
    if (generationStatus === 'generating_videos' || hasVideoOutput) {
      return 'generating_videos';
    }
    return 'starting';
  }, [generationFailed, generationStatus, hasVideoOutput, holdVideoReveal, unlocked]);

  useEffect(() => {
    setResultViewLogged(false);
    highestViewedResultStageRef.current = -1;
    if (initialRouteDocData) {
      setDocumentData(initialRouteDocData);
      setLoading(false);
      setError(null);
      return;
    }

    setDocumentData(null);
    setLoading(true);
    setError(null);
  }, [docId, initialRouteDocData]);

  useEffect(() => {
    let cancelled = false;

    if (!hasStableVideoSet || displayedVideoUrls.length === 0) {
      setVideoPresentationReady(false);
      return () => {
        cancelled = true;
      };
    }

    setVideoPresentationReady(false);
    Promise.all(displayedVideoUrls.map((url) => preloadVideoMetadata(url)))
      .then(() => {
        if (!cancelled) {
          setVideoPresentationReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVideoPresentationReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [displayedVideoUrls, hasStableVideoSet]);

  useEffect(() => {
    if (authLoading || loading || error || unlocked || visibleResultStage !== 'ready_paywall') {
      return;
    }

    const planLogKey = `${docId}:${selectedPlan}`;
    if (paywallPlanLoggedRef.current === planLogKey) {
      return;
    }

    const selectionSource = paywallPlanLoggedRef.current.startsWith(`${docId}:`) ? 'user' : 'default';
    trackResultsEvent('fluffball-paywall-plan-selected', {
      subscription_plan: selectedPlan,
      selection_source: selectionSource,
    });
    paywallPlanLoggedRef.current = planLogKey;
  }, [authLoading, docId, error, loading, selectedPlan, trackResultsEvent, unlocked, visibleResultStage]);

  useEffect(() => {
    const checkoutResult = String(new URLSearchParams(location.search).get('checkout') || '').trim().toLowerCase();
    if (!checkoutResult || !['success', 'cancel'].includes(checkoutResult)) {
      return;
    }

    const checkoutLogKey = `${docId}:${checkoutResult}`;
    if (checkoutReturnLoggedRef.current === checkoutLogKey) {
      return;
    }

    trackResultsEvent('fluffball-checkout-returned', {
      checkout_result: checkoutResult,
    });
    checkoutReturnLoggedRef.current = checkoutLogKey;
  }, [docId, location.search, trackResultsEvent]);

  useEffect(() => {
    if (authLoading || loading || resultViewLogged || !isPageVisible || error || !documentData) {
      return;
    }

    trackResultsEvent('fluffball-result-viewed', {
      signed_in: Boolean(authUser),
      generation_status: generationStatus,
      is_daily_generation: Boolean(documentData?.is_daily_generation),
      unlocked,
      has_output: hasAnyOutput,
      result_stage: visibleResultStage,
      image_previews_visible: showImagePreviewsInFlight,
    });
    setResultViewLogged(true);
  }, [
    authLoading,
    authUser,
    documentData?.is_daily_generation,
    documentData,
    error,
    generationStatus,
    hasAnyOutput,
    isPageVisible,
    loading,
    resultViewLogged,
    showImagePreviewsInFlight,
    trackResultsEvent,
    unlocked,
    visibleResultStage,
  ]);

  useEffect(() => {
    if (authLoading || loading || !isPageVisible || error || !documentData) {
      return;
    }

    const eventName = RESULT_STAGE_VIEW_EVENT_NAMES[visibleResultStage];
    const stageOrder = RESULT_STAGE_VIEW_ORDER[visibleResultStage];
    if (!eventName || !Number.isFinite(stageOrder)) {
      return;
    }

    if (stageOrder <= highestViewedResultStageRef.current) {
      return;
    }

    trackResultsEvent(eventName, {
      signed_in: Boolean(authUser),
      generation_status: generationStatus,
      is_daily_generation: Boolean(documentData?.is_daily_generation),
      unlocked,
      has_output: hasAnyOutput,
      has_image_output: hasImageOutput,
      result_stage: visibleResultStage,
      image_previews_visible: showImagePreviewsInFlight,
    });
    highestViewedResultStageRef.current = stageOrder;
  }, [
    authLoading,
    authUser,
    documentData,
    documentData?.is_daily_generation,
    error,
    generationStatus,
    hasAnyOutput,
    hasImageOutput,
    isPageVisible,
    loading,
    showImagePreviewsInFlight,
    trackResultsEvent,
    unlocked,
    visibleResultStage,
  ]);

  useEffect(() => {
    if (!authUser || !docHasCompletedOutput) {
      return;
    }

    const explicitHomeState = String(userProfile?.home_state || '').trim();
    if (explicitHomeState) {
      cacheHomepageSnapshot(
        authUser,
        buildHomepageSnapshotFromProfile(authUser, userProfile)
      );
      return;
    }

    const fallbackHeroState = getFallbackResultHomeState({
      subscriptionActive,
      documentData,
    });
    cacheHomepageSnapshot(authUser, {
      hasCompletedResult: true,
      latestResultDocId: docId,
      homeActionDocId: docId,
      subscriptionActive,
      canCreateDailyFromHome: fallbackHeroState === 'daily_available',
      heroState: fallbackHeroState,
    });
  }, [
    authUser,
    docHasCompletedOutput,
    docId,
    documentData,
    documentData?.is_daily_generation,
    subscriptionActive,
    userProfile,
  ]);

  const handleAuthSuccess = useCallback(async (user, source) => {
    setAuthUser(user);
    trackResultsEvent('fluffball-sign-in-succeeded', { source }, user);
  }, [trackResultsEvent]);

  const restorePendingResultsRedirect = useCallback(async (isMountedRef = { current: true }) => {
    const emailLinkResult = await consumePendingEmailLinkSignIn();
    if (!isMountedRef.current) {
      return;
    }

    if (emailLinkResult && emailLinkResult.flow === 'results') {
      const source = 'result_email_link';

      if (emailLinkResult.status === 'success' && emailLinkResult.user) {
        setEmailLinkPending(false);
        setAuthInfo('');
        setError('');
        await handleAuthSuccess(emailLinkResult.user, source);
        if (isMountedRef.current) {
          setIsSigningIn(false);
        }
        return;
      }

      setIsSigningIn(false);

      if (emailLinkResult.status === 'awaiting_email') {
        setEmailLinkPending(true);
        setError('');
        setAuthInfo('Enter the email address you used for the sign-in link to finish signing in.');
        return;
      }

      setEmailLinkPending(true);
      setAuthInfo('');
      if (emailLinkResult.status === 'error') {
        console.error('Result email-link sign-in failed:', emailLinkResult.error);
        trackResultsEvent('fluffball-sign-in-failed', {
          source,
          error_message: String(emailLinkResult.error?.message || emailLinkResult.error),
        });
        setError(String(emailLinkResult.error?.message || emailLinkResult.error || 'Could not sign you in right now. Please try again.'));
      }
      return;
    }

    const redirectResult = await consumePendingAuthRedirectResult();
    if (!isMountedRef.current || !redirectResult || redirectResult.flow !== 'results') {
      return;
    }

    const source = redirectResult.providerId === 'apple' ? 'result_apple' : 'result_google';

    if (redirectResult.status === 'success' && redirectResult.user) {
      await handleAuthSuccess(redirectResult.user, source);
      if (isMountedRef.current) {
        setIsSigningIn(false);
      }
      return;
    }

    if (redirectResult.status === 'error') {
      console.error('Result redirect sign-in failed:', redirectResult.error);
      trackResultsEvent('fluffball-sign-in-failed', {
        source,
        error_message: String(redirectResult.error?.message || redirectResult.error),
      });
      setError(String(redirectResult.error?.message || redirectResult.error || 'Could not sign you in right now. Please try again.'));
    } else if (redirectResult.status === 'cancelled') {
      setError('Sign-in was cancelled. Please try again.');
    }

    setIsSigningIn(false);
  }, [handleAuthSuccess, trackResultsEvent]);

  useEffect(() => {
    const isMountedRef = { current: true };
    const runRestore = () => {
      void restorePendingResultsRedirect(isMountedRef);
    };

    if (!authRedirectHandledRef.current) {
      authRedirectHandledRef.current = true;
      runRestore();
    }

    const handlePageShow = () => {
      runRestore();
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [restorePendingResultsRedirect]);

  const handleAuthAction = async ({ action, source, clearEmailLink = true }) => {
    if (isSigningIn) {
      return;
    }

    setIsSigningIn(true);
    setError('');
    setAuthInfo('');
    if (clearEmailLink) {
      setEmailLinkPending(false);
    }
    trackResultsEvent('fluffball-sign-in-clicked', { source });

    try {
      const user = await action();
      if (!user) {
        return;
      }
      await handleAuthSuccess(user, source);
    } catch (signInError) {
      console.error('Sign-in failed:', signInError);
      trackResultsEvent('fluffball-sign-in-failed', {
        source,
        error_message: String(signInError?.message || signInError),
      });
      setError(String(signInError?.message || signInError || 'Could not sign you in right now. Please try again.'));
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleGoogleSignIn = () => handleAuthAction({
    action: () => completeFlowSignInWithGoogle({ redirectFlow: 'results' }),
    source: 'result_google',
  });

  const handleAppleSignIn = () => handleAuthAction({
    action: () => completeFlowSignInWithApple({ redirectFlow: 'results' }),
    source: 'result_apple',
  });

  const handleEmailCreate = ({ email, password }) => handleAuthAction({
    action: () => completeFlowEmailPasswordAuth({
      email,
      password,
      mode: 'create',
    }),
    source: 'result_email_create',
  });

  const handleEmailSignIn = ({ email, password }) => handleAuthAction({
    action: () => completeFlowEmailPasswordAuth({
      email,
      password,
      mode: 'sign_in',
    }),
    source: 'result_email_sign_in',
  });

  const handleEmailLinkSend = ({ email }) => handleAuthAction({
    action: async () => {
      await sendEmailSignInLinkForFlow({
        email,
        flow: 'results',
      });
      setAuthInfo('Check your email for a sign-in link. If it opens in another browser, enter the same email here to finish.');
      return null;
    },
    source: 'result_email_link',
  });

  const handleEmailLinkComplete = ({ email }) => handleAuthAction({
    action: async () => {
      const result = await completePendingEmailLinkSignIn({ email });
      if (result?.status !== 'success' || !result.user) {
        throw result?.error || new Error('This sign-in link is invalid or has already been used.');
      }
      setEmailLinkPending(false);
      return result.user;
    },
    source: 'result_email_link',
    clearEmailLink: false,
  });

  const handleSignOut = async () => {
    try {
      trackResultsEvent('fluffball-sign-out-clicked', {
        source: 'result',
      });
      await signOutCurrentUser();
      setAuthUser(null);
      setDocumentData(null);
      navigate('/', { replace: true });
    } catch (signOutError) {
      console.error('Sign-out failed:', signOutError);
    }
  };

  const handleCheckout = async () => {
    setPaywallError('');
    trackResultsEvent('fluffball-checkout-clicked', {
      has_valid_email: isValidEmail(checkoutEmail),
      pet_type: documentData?.petType || '',
      subscription_plan: selectedPlan,
    });

    const cleanedEmail = checkoutEmail;
    if (!isValidEmail(cleanedEmail)) {
      setPaywallError('No valid account email found. Please sign out and sign back in.');
      return;
    }

    setSavingEmail(true);
    try {
      const resultUrl = getModernResultUrl(docId);
      const checkoutUrl = await openDeferredExternalUrl(async () => {
        const response = await createSubscriptionCheckoutSession({
          docId,
          userUid: authUser?.uid || '',
          customerEmail: cleanedEmail,
          stripeCustomerId: userProfile?.stripe_customer_id || '',
          plan: selectedPlan,
          successUrl: `${resultUrl}?checkout=success`,
          cancelUrl: `${resultUrl}?checkout=cancel`,
        });
        return response?.url || '';
      });
      trackResultsEvent('fluffball-checkout-opened', {
        checkout_url_present: Boolean(checkoutUrl),
        subscription_plan: selectedPlan,
      });
    } catch (checkoutError) {
      console.error('Error preparing checkout:', checkoutError);
      trackResultsEvent('fluffball-checkout-failed', {
        error_message: String(checkoutError?.message || checkoutError),
        subscription_plan: selectedPlan,
      });
      if (String(checkoutError?.message || '').toLowerCase().includes('not signed in')) {
        setPaywallError('Please sign in first, then try checkout again.');
      } else {
        setPaywallError('Could not start checkout. Please try again.');
      }
    } finally {
      setSavingEmail(false);
    }
  };

  const paywallContent = (
    <>
      <div className="text-center">
        <h3 className="break-words text-[1.5rem] leading-[1.05] sm:text-[1.75rem] font-bold text-slate-900">
          <span className="bg-gradient-to-r from-pink-500 via-fuchsia-500 to-purple-500 bg-clip-text text-transparent">
            {paywallHeading}
          </span>
        </h3>
        <p className="mt-1.5 text-[0.95rem] sm:text-[1rem] text-slate-600">
          Plus a brand new one every day.
        </p>
      </div>

      <div className="mt-4 space-y-3">
        {PAYWALL_PLANS.map((plan) => {
          const isSelected = selectedPlan === plan.key;
          return (
            <button
              key={plan.key}
              type="button"
              onClick={() => {
                setSelectedPlan(plan.key);
                setPaywallError('');
              }}
              className={`relative w-full rounded-[1.4rem] px-4 py-4 text-left transition-colors ${
                isSelected
                  ? 'border-[3px] border-purple-400 bg-gradient-to-br from-white to-pink-50 shadow-[0_10px_24px_rgba(217,70,239,0.12)]'
                  : 'border border-pink-100 bg-white hover:border-pink-200'
              }`}
            >
              {plan.badge ? (
                <span className="absolute left-1/2 -top-3 -translate-x-1/2 rounded-full bg-pink-100 px-3 py-1 text-[0.72rem] sm:text-xs font-semibold text-pink-600">
                  {plan.badge}
                </span>
              ) : null}

              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-[1.2rem] sm:text-[1.3rem] font-semibold text-slate-900">
                    {plan.label}
                  </div>
                  {plan.sublabel ? (
                    <div className="mt-0.5 text-[0.95rem] sm:text-[1rem] text-slate-600">
                      {plan.sublabel}
                    </div>
                  ) : null}
                </div>

                <div className="text-right text-slate-900">
                  <span className="text-[1.15rem] sm:text-[1.3rem] font-semibold leading-none">
                    {plan.priceLabel}
                  </span>
                  <span className="text-[0.95rem] sm:text-[1rem] text-slate-600">
                    {plan.priceSuffix}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={handleCheckout}
        disabled={savingEmail}
        className="shine-effect mt-4 w-full text-[1.05rem] sm:text-[1.15rem] px-6 py-3.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold rounded-[1.4rem] shadow-lg hover:from-pink-600 hover:to-purple-600 transition-all duration-300 disabled:opacity-70"
      >
        {savingEmail ? 'Opening checkout...' : unlockButtonText}
      </button>

      <p className="mt-3 text-center text-[0.95rem] sm:text-[1rem] text-slate-500">
        Cancel anytime
      </p>

      {paywallError ? <p className="mt-2 text-center text-xs sm:text-sm text-red-600">{paywallError}</p> : null}
    </>
  );

  const paywallCard = (
    <div className="relative w-full max-w-md bg-gradient-to-b from-white/95 to-pink-50/95 border-2 border-pink-200 rounded-3xl shadow-2xl p-4 sm:p-6 overflow-hidden">
      <div className="absolute -top-4 -left-3 text-3xl opacity-20 rotate-12">🐾</div>
      <div className="absolute -bottom-5 -right-2 text-4xl opacity-20 -rotate-12">🐾</div>
      {paywallContent}
    </div>
  );

  const downloadUrlDirectly = async (url, filename) => {
    if (!url) {
      return;
    }
    await downloadMediaViaBackend({ mediaUrl: url, filename });
  };

  const handleDownloadVideo = async (url, index) => {
    try {
      setDownloadStatus('');
      trackResultsEvent('fluffball-download-video-clicked', {
        video_index: index,
      });
      const downloadResult = await downloadMediaViaBackend({
        mediaUrl: url,
        filename: `${sanitizedPetName}-video-${index + 1}.mp4`,
        preferNativeShare: true,
      });
      trackResultsEvent('fluffball-download-video-succeeded', {
        video_index: index,
        delivery_method: downloadResult?.method || 'download',
      });
    } catch (downloadError) {
      console.error('Could not download video:', downloadError);
      trackResultsEvent('fluffball-download-video-failed', {
        video_index: index,
        error_message: String(downloadError?.message || downloadError),
      });
      setDownloadStatus('Could not download that video yet. Please try again.');
    }
  };

  const handleDownloadAll = async () => {
    if (isDownloadingAll || !unlocked) {
      return;
    }
    setIsDownloadingAll(true);
    setDownloadStatus('');
    try {
      trackResultsEvent('fluffball-download-all-clicked', {
        video_count: videoUrls.length,
        image_count: imageUrls.length,
      });
      const topVideos = videoUrls.slice(0, MAX_RESULT_ASSET_COUNT);
      for (let index = 0; index < topVideos.length; index += 1) {
        await downloadUrlDirectly(topVideos[index], `${sanitizedPetName}-video-${index + 1}.mp4`);
      }
      for (let index = 0; index < imageUrls.length; index += 1) {
        await downloadUrlDirectly(imageUrls[index], `${sanitizedPetName}-photo-${index + 1}.jpg`);
      }
      trackResultsEvent('fluffball-download-all-succeeded', {
        video_count: videoUrls.length,
        image_count: imageUrls.length,
      });
    } catch (downloadError) {
      console.error('Could not download all media:', downloadError);
      trackResultsEvent('fluffball-download-all-failed', {
        error_message: String(downloadError?.message || downloadError),
      });
      setDownloadStatus('Could not download all videos and photos. Please try again.');
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const resolveShareLink = async () => {
    if (shareUrl) {
      return shareUrl;
    }
    const created = await createShareLink({ docId });
    const nextShareId = String(created?.share_id || '').trim();
    const nextShareUrl =
      String(created?.share_url || '').trim() || `${window.location.origin}/share/${nextShareId}`;
    if (nextShareId) {
      setDocumentData((previous) => ({
        ...(previous || {}),
        share_id: nextShareId,
        share_url: nextShareUrl,
      }));
    }
    return nextShareUrl;
  };

  const sharePreviewLink = async () => {
    setShareStatus('');
    try {
      trackResultsEvent('fluffball-share-clicked', {
        share_text_present: false,
      });
      const resolvedShareUrl = await resolveShareLink();
      if (!resolvedShareUrl) {
        throw new Error('Share URL unavailable');
      }

      if (navigator.share) {
        await navigator.share({
          url: resolvedShareUrl,
        });
        trackResultsEvent('fluffball-share-succeeded', {
          share_method: 'navigator_share',
        });
        setShareStatus('Share sheet opened.');
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(resolvedShareUrl);
        trackResultsEvent('fluffball-share-succeeded', {
          share_method: 'clipboard',
        });
        setShareStatus('Share link copied.');
      } else {
        trackResultsEvent('fluffball-share-succeeded', {
          share_method: 'fallback_url',
        });
        setShareStatus(resolvedShareUrl);
      }
    } catch (shareError) {
      console.error('Failed to share result:', shareError);
      trackResultsEvent('fluffball-share-failed', {
        error_message: String(shareError?.message || shareError),
      });
      setShareStatus('Could not create a share link yet. Please try again.');
    }
  };

  const handleShareVideo = async () => {
    await sharePreviewLink();
  };

  const videoGallery = (
    <div
      className={`relative rounded-2xl overflow-hidden mx-auto ${
        isSingleVideoLayout ? 'max-w-sm' : 'max-w-2xl'
      }`}
    >
      <div className={isSingleVideoLayout ? 'flex justify-center' : 'grid grid-cols-1 sm:grid-cols-2 gap-3'}>
        {displayedVideoUrls.map((url, index) => (
          <div
            key={`${url}-${index}`}
            className={`${isSingleVideoLayout ? 'w-full max-w-sm' : ''} relative`}
          >
            <MediaCard
              url={url}
              type="video"
              posterUrl={displayedImageUrls[index] || ''}
              locked={!unlocked}
              canDownload={unlocked}
              onDownload={() => handleDownloadVideo(url, index)}
              onDownloadAll={handleDownloadAll}
              downloadAllDisabled={isDownloadingAll}
              showShare={unlocked}
              onShare={handleShareVideo}
              shareLabel="Share"
            />
          </div>
        ))}
      </div>

      {!unlocked && (
        <div className="absolute inset-0 hidden sm:flex backdrop-blur-[2px] items-center justify-center p-6">
          {paywallCard}
        </div>
      )}
    </div>
  );

  if (authLoading || (authUser && !authFirestoreReady)) {
    return <SimpleSpinnerScreen fullscreen={true} />;
  }

  if (!authUser) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-2xl">
          <AuthGateStep
            error={error}
            info={authInfo}
            initialMode="sign_in"
            isLoading={isSigningIn}
            showSocialAuth={!prefersMagicLinkEmailAuth}
            emailAuthMode={
              emailLinkPending
                ? 'magic_link_complete'
                : (prefersMagicLinkEmailAuth ? 'magic_link_send' : 'password')
            }
            onApple={handleAppleSignIn}
            onClearError={() => {
              setError('');
              setAuthInfo('');
            }}
            onEmailCreate={handleEmailCreate}
            onEmailSignIn={handleEmailSignIn}
            onEmailLinkSend={handleEmailLinkSend}
            onEmailLinkComplete={handleEmailLinkComplete}
            onGoogle={handleGoogleSignIn}
            signInTitle="Sign in required"
            subtitle={
              prefersMagicLinkEmailAuth
                ? 'Sign in with email to access your generation results. We\'ll send you a secure sign-in link.'
                : 'Use Apple, Google, or email to access your generation results.'
            }
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return <SimpleSpinnerScreen fullscreen={true} />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white border border-red-200 rounded-2xl shadow-sm p-6">
          <p className="text-center text-red-600 mb-4">Error: {error}</p>
          <div className="flex gap-3 justify-center">
            {canAccessProfile ? (
              <a href="/profile" className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50">
                Profile
              </a>
            ) : null}
            <button onClick={handleSignOut} className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black">
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <AppHeader>
        {canAccessProfile ? (
          <a href="/profile" className={appHeaderActionClasses}>
            Profile
          </a>
        ) : null}
        <button className={appHeaderActionClasses} onClick={handleSignOut}>
          Sign out
        </button>
      </AppHeader>

      <div
        className={`container mx-auto p-4 max-w-6xl ${showMobilePinnedPaywall ? 'pb-72 sm:pb-4' : ''}`}
      >
        {(!hasVideoOutput || holdVideoReveal) && !generationFailed && (
          <div className="bg-white rounded-2xl border border-blue-100 shadow-sm p-8 mt-6">
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">{generatingHeading}</h1>
          <p className="text-gray-600 mb-5">
            This should take about 1 to 2 minutes.
          </p>
          <div className="w-full bg-purple-100 rounded-full h-3">
            <div
              className="progress-bar-fill h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-3 text-sm text-gray-600">
            <span>{statusLabel(documentData)}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          </div>
        )}

        {showImagePreviewsInFlight && (
          <section className="mt-8">
            <div className="max-w-4xl mx-auto">
              <div className={isSingleImageLayout ? 'flex justify-center' : 'grid grid-cols-1 sm:grid-cols-2 gap-3'}>
                {displayedImageUrls.map((url, index) => (
                  <div
                    key={`${url}-${index}`}
                    className={`${isSingleImageLayout ? 'w-full max-w-sm' : ''} result-preview-breathe will-change-transform`}
                  >
                    <MediaCard
                      url={url}
                      type="image"
                      locked={true}
                      canDownload={false}
                      showShare={false}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {generationFailed && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mt-6">
          Generation failed. Please try again in a minute.
          {documentData?.generation_error ? (
            <p className="mt-2 text-xs break-words">{documentData.generation_error}</p>
          ) : null}
          </div>
        )}

        {hasVideoOutput && !holdVideoReveal && (
          <>
            {videoUrls.length > 0 && (
              <section className={showMobilePinnedPaywall ? 'mt-3 sm:mt-8' : 'mt-8'}>
              {showMobilePinnedPaywall && paywallPreviewVideoUrl ? (
                <>
                  <div className="sm:hidden mx-auto max-w-sm">
                    <MediaCard
                      url={paywallPreviewVideoUrl}
                      type="video"
                      posterUrl={displayedImageUrls[0] || ''}
                      locked={true}
                      canDownload={false}
                      showShare={false}
                    />
                  </div>
                  <div className="hidden sm:block">
                    {videoGallery}
                  </div>
                </>
              ) : videoGallery}
              {unlocked && (shareStatus || downloadStatus) ? (
                <div className="max-w-2xl mx-auto mt-3 flex flex-col items-center">
                  {shareStatus ? <p className="mt-1 text-xs text-gray-600">{shareStatus}</p> : null}
                  {downloadStatus ? <p className="mt-1 text-xs text-red-600">{downloadStatus}</p> : null}
                </div>
              ) : null}
              </section>
            )}
          </>
        )}

        <LegalFooter className="mt-10" />
      </div>

      {showMobilePinnedPaywall ? (
        <div className="sm:hidden fixed inset-x-0 bottom-0 z-30 pointer-events-none">
          <div
            className="pointer-events-auto rounded-t-[2rem] bg-white shadow-[0_-16px_40px_rgba(15,23,42,0.12)]"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)' }}
          >
            <div className="px-4 pt-3">
              {paywallContent}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ResultsPage;
